import React, { useState, useEffect } from 'react';
import Icon from '../Shared/Icons';
import { db } from '../../config/firebase';
import { doc, getDoc } from 'firebase/firestore';

export default function DonateModal({ onClose, forWhom }) {
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState('');
  const [activeTab, setActiveTab] = useState('bank');

  useEffect(() => {
    fetchPaymentDetails();
  }, []);

  async function fetchPaymentDetails() {
    try {
      const docRef = doc(db, 'settings', 'payments');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setPayment(docSnap.data());
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }

  function copyToClipboard(text, label) {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  }

  const tabs = [
    { id: 'bank', icon: 'card', label: 'Bank' },
    { id: 'mobile', icon: 'bolt', label: 'Mobile' },
    { id: 'paypal', icon: 'world', label: 'PayPal' },
    { id: 'other', icon: 'heart', label: 'Other' },
  ];

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div
        style={styles.modal}
        onClick={e => e.stopPropagation()}
      >

        {/* HEADER */}
        <div style={styles.modalHeader}>
          <div>
            <div style={styles.modalTitle}>
              Make a Donation
            </div>
            <div style={styles.modalSub}>
              {forWhom ? `Supporting ${forWhom}` : 'Choose your preferred payment method'}
            </div>
          </div>
          <button
            style={styles.closeBtn}
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        {loading ? (
          <div style={styles.loading}>
            Loading payment details...
          </div>
        ) : !payment ? (
          <div style={styles.noPayment}>
            Payment details not set up yet.
            Please contact the foundation directly.
          </div>
        ) : (
          <>
            {/* TABS */}
            <div style={styles.tabs}>
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  style={{
                    ...styles.tab,
                    ...(activeTab === tab.id
                      ? styles.tabActive
                      : {})
                  }}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.icon} {tab.label}
                </button>
              ))}
            </div>

            {/* BANK TAB */}
            {activeTab === 'bank' && (
              <div style={styles.tabContent}>
                {payment.bankName ? (
                  <>
                    <div style={styles.paymentCard}>
                      <div style={styles.paymentCardHeader}>
                        <div style={styles.bankIcon}></div>
                        <div style={styles.bankName}>
                          {payment.bankName}
                        </div>
                      </div>
                      {[
                        { label: 'Account Name', value: payment.accountName },
                        { label: 'Account Number', value: payment.accountNumber },
                        { label: 'Branch', value: payment.branchName },
                        { label: 'Swift Code', value: payment.swiftCode },
                      ].map((item, i) => item.value && (
                        <div key={i} style={styles.detailRow}>
                          <div style={styles.detailLabel}>
                            {item.label}
                          </div>
                          <div style={styles.detailRight}>
                            <div style={styles.detailValue}>
                              {item.value}
                            </div>
                            <button
                              style={{
                                ...styles.copyBtn,
                                ...(copied === item.label
                                  ? styles.copyBtnSuccess
                                  : {})
                              }}
                              onClick={() => copyToClipboard(
                                item.value,
                                item.label
                              )}
                            >
                              {copied === item.label
                                ? 'Copied!'
                                : 'Copy'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div style={styles.notAvailable}>
                    Bank details not available yet.
                  </div>
                )}
              </div>
            )}

            {/* MOBILE TAB */}
            {activeTab === 'mobile' && (
              <div style={styles.tabContent}>
                {[
                  {
                    name: 'M-Pesa',
                    icon: '',
                    number: payment.mpesaNumber,
                    accountName: payment.mpesaName,
                    color: '#00A651',
                  },
                  {
                    name: 'Tigo Pesa',
                    icon: '',
                    number: payment.tigoPesaNumber,
                    accountName: payment.tigoPesaName,
                    color: '#0099CC',
                  },
                  {
                    name: 'Airtel Money',
                    icon: '',
                    number: payment.airtelNumber,
                    accountName: payment.airtelName,
                    color: '#FF0000',
                  },
                ].map((m, i) => m.number && (
                  <div key={i} style={styles.mobileCard}>
                    <div style={{
                      ...styles.mobileBadge,
                      background: m.color,
                    }}>
                      {m.name}
                    </div>
                    <div style={styles.detailRow}>
                      <div style={styles.detailLabel}>
                        Number
                      </div>
                      <div style={styles.detailRight}>
                        <div style={styles.detailValue}>
                          {m.number}
                        </div>
                        <button
                          style={{
                            ...styles.copyBtn,
                            ...(copied === m.name
                              ? styles.copyBtnSuccess
                              : {})
                          }}
                          onClick={() => copyToClipboard(
                            m.number,
                            m.name
                          )}
                        >
                          {copied === m.name
                            ? 'Copied!'
                            : 'Copy'}
                        </button>
                      </div>
                    </div>
                    <div style={styles.detailRow}>
                      <div style={styles.detailLabel}>
                        Account Name
                      </div>
                      <div style={styles.detailValue}>
                        {m.accountName}
                      </div>
                    </div>
                  </div>
                ))}
                {!payment.mpesaNumber &&
                 !payment.tigoPesaNumber &&
                 !payment.airtelNumber && (
                  <div style={styles.notAvailable}>
                    Mobile money details not available yet.
                  </div>
                )}
              </div>
            )}

            {/* PAYPAL TAB */}
            {activeTab === 'paypal' && (
              <div style={styles.tabContent}>
                {payment.paypalEmail ? (
                  <div style={styles.paymentCard}>
                    <div style={styles.paymentCardHeader}>
                      <div style={styles.bankIcon}></div>
                      <div style={styles.bankName}>PayPal</div>
                    </div>
                    <div style={styles.detailRow}>
                      <div style={styles.detailLabel}>
                        PayPal Email
                      </div>
                      <div style={styles.detailRight}>
                        <div style={styles.detailValue}>
                          {payment.paypalEmail}
                        </div>
                        <button
                          style={{
                            ...styles.copyBtn,
                            ...(copied === 'paypal'
                              ? styles.copyBtnSuccess
                              : {})
                          }}
                          onClick={() => copyToClipboard(
                            payment.paypalEmail,
                            'paypal'
                          )}
                        >
                          {copied === 'paypal'
                            ? 'Copied!'
                            : 'Copy'}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={styles.notAvailable}>
                    PayPal not available yet.
                  </div>
                )}
              </div>
            )}

            {/* OTHER TAB */}
            {activeTab === 'other' && (
              <div style={styles.tabContent}>
                {payment.otherMethod ? (
                  <div style={styles.paymentCard}>
                    <div style={styles.paymentCardHeader}>
                      <div style={styles.bankIcon}></div>
                      <div style={styles.bankName}>
                        {payment.otherMethod}
                      </div>
                    </div>
                    <div style={styles.otherDetails}>
                      {payment.otherDetails}
                    </div>
                  </div>
                ) : (
                  <div style={styles.notAvailable}>
                    Other payment methods not available yet.
                  </div>
                )}
              </div>
            )}

            {/* INSTRUCTIONS */}
            {payment.instructions && (
              <div style={styles.instructions}>
                <div style={styles.instructionsTitle}>
                  Important Instructions
                </div>
                <div style={styles.instructionsText}>
                  {payment.instructions}
                </div>
              </div>
            )}

          </>
        )}

        {/* THANK YOU */}
        <div style={styles.thankYou}>
          <div style={styles.thankYouText}>
            Thank you for supporting Iyanna Foundation 
          </div>
          <div style={styles.thankYouSub}>
            Your contribution changes lives
          </div>
        </div>

      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
    backdropFilter: 'blur(4px)',
  },
  modal: {
    background: '#fff',
    borderRadius: '20px',
    width: '100%',
    maxWidth: '500px',
    maxHeight: '85vh',
    overflowY: 'auto',
    boxShadow: '0 24px 60px rgba(0,0,0,0.3)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '24px 24px 0',
    marginBottom: '20px',
  },
  modalTitle: {
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: '22px',
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  modalSub: {
    fontSize: '12.5px',
    color: '#6B7280',
    marginTop: '3px',
  },
  closeBtn: {
    background: '#F3F4F6',
    border: 'none',
    borderRadius: '50%',
    width: '32px',
    height: '32px',
    cursor: 'pointer',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'inherit',
    flexShrink: 0,
  },
  loading: {
    textAlign: 'center',
    padding: '30px',
    color: '#6B7280',
    fontSize: '13px',
  },
  noPayment: {
    textAlign: 'center',
    padding: '30px',
    color: '#6B7280',
    fontSize: '13px',
    lineHeight: 1.6,
  },
  tabs: {
    display: 'flex',
    gap: '8px',
    padding: '0 24px',
    marginBottom: '16px',
    flexWrap: 'wrap',
  },
  tab: {
    padding: '8px 16px',
    borderRadius: '20px',
    border: '1.5px solid #E5E7EB',
    background: 'transparent',
    color: '#6B7280',
    fontSize: '12.5px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.2s',
  },
  tabActive: {
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    borderColor: 'transparent',
    color: '#fff',
  },
  tabContent: {
    padding: '0 24px',
    marginBottom: '16px',
  },
  paymentCard: {
    background: '#F9FAFB',
    borderRadius: '14px',
    padding: '18px',
    border: '1px solid #E5E7EB',
  },
  paymentCardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '14px',
    paddingBottom: '12px',
    borderBottom: '1px solid #E5E7EB',
  },
  bankIcon: {
    fontSize: '24px',
  },
  bankName: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#1C1C1E',
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
    borderBottom: '1px solid #F3F4F6',
  },
  detailLabel: {
    fontSize: '12px',
    color: '#6B7280',
  },
  detailRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  detailValue: {
    fontSize: '13px',
    fontWeight: '500',
    color: '#1C1C1E',
  },
  copyBtn: {
    background: '#EAF3EC',
    color: '#14452F',
    border: 'none',
    padding: '4px 10px',
    borderRadius: '6px',
    fontSize: '11px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontWeight: '500',
    transition: 'all 0.2s',
  },
  copyBtnSuccess: {
    background: '#14452F',
    color: '#fff',
  },
  mobileCard: {
    background: '#F9FAFB',
    borderRadius: '14px',
    padding: '16px',
    border: '1px solid #E5E7EB',
    marginBottom: '12px',
  },
  mobileBadge: {
    display: 'inline-block',
    color: '#fff',
    fontSize: '11px',
    fontWeight: '600',
    padding: '3px 10px',
    borderRadius: '20px',
    marginBottom: '12px',
  },
  otherDetails: {
    fontSize: '13px',
    color: '#374151',
    lineHeight: 1.6,
    marginTop: '8px',
  },
  instructions: {
    margin: '0 24px 16px',
    background: 'linear-gradient(135deg, #f1f8f1, #e8f5e9)',
    borderRadius: '12px',
    padding: '16px',
    border: '1px solid #c8e6c9',
  },
  instructionsTitle: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#14452F',
    marginBottom: '8px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  instructionsText: {
    fontSize: '13px',
    color: '#374151',
    lineHeight: 1.6,
  },
  thankYou: {
    background: 'linear-gradient(135deg, #0B3524, #175239)',
    borderRadius: '0 0 20px 20px',
    padding: '20px 24px',
    textAlign: 'center',
  },
  thankYouText: {
    color: '#fff',
    fontSize: '14px',
    fontWeight: '600',
    fontFamily: "'Fraunces', Georgia, serif",
  },
  thankYouSub: {
    color: 'rgba(255,255,255,0.5)',
    fontSize: '12px',
    marginTop: '4px',
  },
  notAvailable: {
    textAlign: 'center',
    color: '#6B7280',
    fontSize: '13px',
    padding: '20px 0',
  },
};